import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";

import {
  generateFlightAiFeedback,
  getFlightAiFeedback,
  getFlightCharts,
  getFlightDetail,
  getFlightLaps,
  getFlightMetrics,
  getFlightReplay2d,
  getFlightUserInputs,
  type FlightDetail,
  type FlightLap,
  type FlightMetrics,
  type FlightUserInput,
  type ChartSample,
  type ReplayPoint,
} from "@/api/flightDetail";
import { PageHeader } from "@/components/PageHeader";
import { SearchableSelect } from "@/components/SearchableSelect";
import { RouteMap } from "@/components/RouteMap";
import { TrainingPopup } from "@/components/TrainingPopup";
import { WeatherCard } from "@/components/WeatherCard";
import { Cesium3DMap } from "@/components/Cesium3DMap";
import type { CesiumCameraMode, CesiumMapStyle } from "@/components/Cesium3DMap";
import { HudWidgets } from "@/components/HudWidgets";
import { ReplayControls } from "@/components/ReplayControls";
import { TelemetryMetricChart } from "@/components/TelemetryMetricChart";
import { useReplayPlayer } from "@/hooks/useReplayPlayer";
import { useHudConfig, HUD_WIDGET_LABELS, type HudWidgetKey } from "@/hooks/useHudConfig";
import { formatDate, formatDuration } from "@/lib/format";
import { HttpError } from "@/api/client";
import { getAuthOptions } from "@/api/auth";
import type { TimedPoint } from "@/lib/replay-core";
import { computeLapPassTimes } from "@/lib/lap-tracking";

type TabKey = "overview" | "insight" | "expert" | "laps" | "replay" | "user_inputs";

const HIDE_LAPS_USER_INPUTS =
  String(import.meta.env.VITE_HIDE_LAPS_USER_INPUTS ?? "").toLowerCase() === "true";
const VISIBLE_TABS: TabKey[] = HIDE_LAPS_USER_INPUTS
  ? ["overview", "insight", "expert", "replay"]
  : ["overview", "insight", "expert", "laps", "replay", "user_inputs"];
type ExpertSubtab = "performance" | "gmeter" | "weather";
type ReplayViewMode = "3d" | "2d";

const MPS_TO_KMH = 3.6;

interface Entity {
  id: number;
  source_entity_id: number | null;
  urn: string | null;
  class_name: string | null;
  entity_type: string | null;
  is_primary: boolean;
}

interface AnalysisSafety {
  peak_acceleration_mps2: number | null;
  peak_turn_rate_deg_s: number | null;
  high_acceleration_events: number | null;
  rapid_turn_exposure_pct: number | null;
  hard_bank_exposure_pct: number | null;
  high_descent_exposure_pct: number | null;
  high_descent_exposure_s: number | null;
  damage_peak_pct: number | null;
  low_fuel_exposure_pct: number | null;
  engine_off_aloft_pct: number | null;
  training_ceiling_m: number | null;
}

interface AnalysisSystems {
  engine_on_ratio_pct: number | null;
  fuel_start_pct: number | null;
  fuel_end_pct: number | null;
  fuel_delta_pct: number | null;
  max_model_speed_mps: number | null;
}

interface AnalysisAttitude {
  max_bank_abs_deg: number | null;
}

interface DataQuality {
  sample_count: number | null;
  median_interval_s: number | null;
  max_gap_s: number | null;
  duplicate_timestamps: number | null;
  position_coverage_pct: number | null;
  orientation_coverage_pct: number | null;
}

interface Analysis {
  safety?: AnalysisSafety | null;
  systems?: AnalysisSystems | null;
  attitude?: AnalysisAttitude | null;
  data_quality?: DataQuality | null;
}

function num(
  value: number | null | undefined,
  digits = 0,
  suffix = "",
): string {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(digits)}${suffix}`;
}

function speedKmh(value: number | null | undefined, digits = 1, suffix = " km / h"): string {
  if (value === null || value === undefined) return "—";
  return `${(value * MPS_TO_KMH).toFixed(digits)}${suffix}`;
}

function normalizeFuelPercent(
  fuel: number | null | undefined,
  fuelCapacity: number | null | undefined,
): number | null {
  if (fuel == null) return null;
  const numericFuel = Number(fuel);
  const numericCapacity = fuelCapacity == null ? null : Number(fuelCapacity);
  if (
    numericFuel >= 0 &&
    numericFuel <= 1 &&
    (numericCapacity == null || numericCapacity > 1)
  ) {
    return numericFuel * 100;
  }
  if (numericCapacity == null || numericCapacity === 0) {
    return numericFuel;
  }
  return (numericFuel / numericCapacity) * 100;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/**
 * Radar chart summarizing the flight across 5 dimensions. Values are mapped to
 * 0–100 using training-range heuristics so one bad axis doesn't flatten the shape.
 *
 * The axes are meant as a quick visual fingerprint — NOT a precise score.
 */
function FlightRadarChart({
  metrics,
  analysis,
}: {
  metrics: FlightMetrics | null;
  analysis: Analysis;
}) {
  const body = typeof document !== "undefined" ? getComputedStyle(document.body) : null;
  const primary = body?.getPropertyValue("--primary").trim() || "#FF6B1A";
  const textMuted = body?.getPropertyValue("--text-muted").trim() || "#A0AAB8";
  const border = body?.getPropertyValue("--border").trim() || "rgba(42,52,68,0.5)";

  const smoothness = metrics?.smoothness_score ?? null;
  const avgSpeedKmh =
    metrics?.avg_speed_mps != null ? metrics.avg_speed_mps * MPS_TO_KMH : null;
  const climbM = metrics?.total_climb_m ?? null;
  const turnRate = metrics?.max_turn_rate_deg_s ?? null;
  const hoverRatio = metrics?.hover_ratio ?? null;

  const peakG =
    analysis.safety?.peak_acceleration_mps2 != null
      ? analysis.safety.peak_acceleration_mps2 / 9.81
      : null;

  // 0–100 scoring — each axis has its own reference range.
  const smoothnessScore = smoothness != null ? clamp01(smoothness / 100) * 100 : 0;
  const speedScore = avgSpeedKmh != null ? clamp01(avgSpeedKmh / 200) * 100 : 0;
  const climbScore = climbM != null ? clamp01(climbM / 400) * 100 : 0;
  const agilityScore =
    turnRate != null
      ? clamp01(turnRate / 180) * 100
      : peakG != null
        ? clamp01(peakG / 3) * 100
        : 0;
  const controlScore = hoverRatio != null ? clamp01(1 - hoverRatio) * 100 : 50;

  const option = {
    animation: false,
    tooltip: {
      trigger: "item" as const,
      backgroundColor: "rgba(10,14,20,0.96)",
      borderColor: border,
      textStyle: { color: "#E8ECF1", fontSize: 12 },
    },
    radar: {
      shape: "polygon" as const,
      center: ["50%", "52%"],
      radius: "68%",
      splitNumber: 4,
      axisName: {
        color: textMuted,
        fontSize: 11,
        fontFamily: "Rajdhani, Barlow Condensed, Inter, sans-serif",
      },
      splitLine: { lineStyle: { color: border } },
      splitArea: { show: false },
      axisLine: { lineStyle: { color: border } },
      indicator: [
        { name: "SMOOTHNESS", max: 100 },
        { name: "SPEED", max: 100 },
        { name: "CLIMB", max: 100 },
        { name: "AGILITY", max: 100 },
        { name: "CONTROL", max: 100 },
      ],
    },
    series: [
      {
        type: "radar" as const,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: { width: 2, color: primary },
        itemStyle: { color: primary },
        areaStyle: { color: "rgba(255, 107, 26, 0.2)" },
        tooltip: {
          formatter: () =>
            `Smoothness: ${smoothnessScore.toFixed(0)}<br/>` +
            `Speed: ${speedScore.toFixed(0)}<br/>` +
            `Climb: ${climbScore.toFixed(0)}<br/>` +
            `Agility: ${agilityScore.toFixed(0)}<br/>` +
            `Control: ${controlScore.toFixed(0)}`,
        },
        data: [
          {
            value: [
              smoothnessScore,
              speedScore,
              climbScore,
              agilityScore,
              controlScore,
            ],
            name: "Flight",
          },
        ],
      },
    ],
  };

  return (
    <div className="overview-radar">
      <ReactECharts option={option} notMerge style={{ height: 200, width: "100%" }} />
    </div>
  );
}

function classifySmoothness(score: number | null | undefined): string {
  if (score === null || score === undefined) return "Not enough telemetry to calculate a score";
  if (score >= 80) return "Steady, controlled flight";
  if (score >= 60) return "Mostly smooth with some sharper inputs";
  return "More abrupt movement or uneven telemetry";
}

export function FlightDetailPage() {
  const { id } = useParams<{ id: string }>();
  const flightId = Number(id);
  const [params, setParams] = useSearchParams();
  const requestedTab = (params.get("tab") as TabKey) || "overview";
  const activeTab: TabKey = VISIBLE_TABS.includes(requestedTab) ? requestedTab : "overview";
  const entityParam = params.get("entity_id");

  const setTab = (tab: TabKey) => {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    setParams(next);
  };

  const setEntity = (entityId: string) => {
    const next = new URLSearchParams(params);
    next.set("entity_id", entityId);
    setParams(next);
  };

  const detailQ = useQuery({
    queryKey: ["flight", flightId],
    queryFn: () => getFlightDetail(flightId),
    enabled: Number.isFinite(flightId),
  });

  if (detailQ.isLoading) return <p className="empty-state">Loading flight…</p>;
  if (detailQ.isError) {
    return (
      <div className="callout danger">
        <strong>Could not load flight</strong>
        <p>{detailQ.error instanceof Error ? detailQ.error.message : "Unknown error"}</p>
      </div>
    );
  }
  if (!detailQ.data) return null;

  const flight = detailQ.data;
  const entities = (flight.entities ?? []) as unknown as Entity[];
  const selectedEntity =
    entities.find((e) => entityParam && String(e.id) === entityParam) ??
    entities.find((e) => e.is_primary) ??
    entities[0] ??
    null;

  return (
    <>
      <FlightHeader
        flight={flight}
        entities={entities}
        selectedEntityId={selectedEntity?.id ?? null}
        activeTab={activeTab}
        onTabChange={setTab}
        onEntityChange={setEntity}
      />

      {activeTab === "overview" && (
        <OverviewTab flightId={flightId} flight={flight} selectedEntity={selectedEntity} />
      )}
      {activeTab === "insight" && <InsightTab flightId={flightId} />}
      {activeTab === "replay" && <ReplayTab flightId={flightId} />}
      {activeTab === "expert" && <ExpertTab flightId={flightId} flight={flight} />}
      {!HIDE_LAPS_USER_INPUTS && activeTab === "laps" && <LapsTab flightId={flightId} />}
      {!HIDE_LAPS_USER_INPUTS && activeTab === "user_inputs" && (
        <UserInputsTab flightId={flightId} />
      )}
    </>
  );
}

function FlightHeader({
  flight,
  entities,
  selectedEntityId,
  activeTab,
  onTabChange,
  onEntityChange,
}: {
  flight: FlightDetail;
  entities: Entity[];
  selectedEntityId: number | null;
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
  onEntityChange: (entityId: string) => void;
}) {
  const missionLine =
    flight.mission_name || flight.map_name ? (
      <>
        {flight.mission_name}
        {flight.mission_name && flight.map_name && " · "}
        {flight.map_name}
      </>
    ) : null;
  return (
    <>
      <PageHeader
        title={flight.title ?? flight.session_name ?? `Flight ${flight.id}`}
        lede={missionLine}
        actions={
          <div className="flight-header-tags">
            <div className="flight-chip flight-chip--meta" title="Flight duration">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <polyline points="12 7 12 12 15 14" />
              </svg>
              <span>{formatDuration(flight.duration_seconds)}</span>
            </div>
            <span className={`status-pill ${flight.status}`}>{flight.status}</span>
            {entities.length > 0 && selectedEntityId !== null && (
              <SearchableSelect
                className="flight-entity-select"
                value={String(selectedEntityId)}
                onChange={onEntityChange}
                size="compact"
                minWidth={180}
                maxWidth={280}
                ariaLabel="Select tracked entity for analysis"
                searchPlaceholder="Search entity…"
                searchable={entities.length > 6}
                options={entities.map((entity) => ({
                  value: String(entity.id),
                  label: `${entity.source_entity_id} · ${entity.class_name ?? entity.entity_type ?? entity.urn}`,
                }))}
              />
            )}
            <a
              className="button primary flight-download-btn"
              href={`/api/flights/${flight.id}/download`}
              title="Download the raw DuckDB telemetry file"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Download</span>
            </a>
          </div>
        }
      />
      <nav className="flight-tabs" id="debrief-bar">
        {VISIBLE_TABS.map((tab) => (
          <button
            key={tab}
            className={`tab-button${activeTab === tab ? " active" : ""}`}
            type="button"
            onClick={() => onTabChange(tab)}
          >
            {tab === "overview" && "Overview"}
            {tab === "insight" && "AI Insight"}
            {tab === "expert" && "Expert View"}
            {tab === "laps" && "Laps"}
            {tab === "replay" && "Replay"}
            {tab === "user_inputs" && "User Inputs"}
          </button>
        ))}
      </nav>
    </>
  );
}

function OverviewTab({
  flightId,
  flight,
  selectedEntity,
}: {
  flightId: number;
  flight: FlightDetail;
  selectedEntity: Entity | null;
}) {
  const aiQ = useQuery({
    queryKey: ["flight", flightId, "ai"],
    queryFn: () => getFlightAiFeedback(flightId),
    retry: false,
  });
  const replayQ = useQuery({
    queryKey: ["flight", flightId, "replay-2d"],
    queryFn: () => getFlightReplay2d(flightId),
  });

  const metrics = (flight as unknown as { metrics?: FlightMetrics | null }).metrics ?? null;
  const analysis = ((flight as unknown as { analysis?: Analysis | null }).analysis ?? {}) as Analysis;
  const weather = (flight.weather_summary ?? null) as Parameters<typeof WeatherCard>[0]["summary"] | null;
  const structures = (flight.structures ?? []) as unknown as Parameters<typeof RouteMap>[0]["structures"];
  const homePoint =
    ((flight as unknown as { home_point?: { latitude: number; longitude: number; label?: string } | null })
      .home_point) ?? null;

  const path = useMemo(() => {
    if (!replayQ.data) return [];
    return replayQ.data.points.map((p: ReplayPoint) => [p.latitude, p.longitude]);
  }, [replayQ.data]);

  const aiReady = aiQ.data?.status === "ready";
  const aiSummary = aiReady ? aiQ.data?.summary : null;
  const aiMessage = (aiQ.data && !aiReady && (aiQ.data as { message?: string }).message) ||
    "No AI feedback yet. Generate it in the AI Insight tab.";

  const smoothness = metrics?.smoothness_score ?? null;
  const fuelStart = analysis.systems?.fuel_start_pct;
  const fuelEnd = analysis.systems?.fuel_end_pct;
  const peakG =
    metrics?.max_acceleration_mps2 !== null && metrics?.max_acceleration_mps2 !== undefined
      ? metrics.max_acceleration_mps2 / 9.81
      : null;

  const hasWeather = Boolean(weather && weather.sample_count);

  return (
    <section className="tab-panel active overview-layout">
      {/* Top metric strip: 6 compact tiles across the full width. */}
      <div className="overview-metrics">
        <article className="overview-metric">
          <span>Distance</span>
          <strong>{num(metrics?.total_distance_m, 0, " m")}</strong>
        </article>
        <article className="overview-metric">
          <span>Avg speed</span>
          <strong>{speedKmh(metrics?.avg_speed_mps)}</strong>
        </article>
        <article className="overview-metric">
          <span>Max speed</span>
          <strong>{speedKmh(metrics?.max_speed_mps)}</strong>
        </article>
        <article className="overview-metric">
          <span>Altitude</span>
          <strong>
            {num(metrics?.min_altitude_m, 0)}–{num(metrics?.max_altitude_m, 0, " m")}
          </strong>
        </article>
        <article className="overview-metric">
          <span>Battery</span>
          <strong>
            {fuelStart != null && fuelEnd != null
              ? `${num(fuelStart, 0, "%")} → ${num(fuelEnd, 0, "%")}`
              : fuelEnd != null
                ? num(fuelEnd, 0, "%")
                : "—"}
          </strong>
        </article>
        <article className="overview-metric">
          <span>Peak G</span>
          <strong>{num(peakG, 2, " g")}</strong>
        </article>
      </div>

      {/* Main two-column area: left column stacks AI / smoothness, right is the big map. */}
      <div className="overview-grid">
        <div className="overview-grid__left">
          <article className="overview-card overview-card--ai">
            <header>
              <span className="overview-card__eyebrow">AI summary</span>
              {selectedEntity?.urn && (
                <span className="overview-card__meta" title="Entity URN">
                  {selectedEntity.urn}
                </span>
              )}
            </header>
            {aiSummary ? (
              <p className="overview-card__body">{aiSummary}</p>
            ) : (
              <p className="overview-card__body overview-card__body--muted">{aiMessage}</p>
            )}
          </article>

          <article className="overview-card overview-card--smoothness">
            <header>
              <span className="overview-card__eyebrow">Smoothness score</span>
              <span className="overview-smoothness-big">{num(smoothness, 0, "/100")}</span>
            </header>
            <p className="overview-card__body overview-card__body--muted">
              {classifySmoothness(smoothness)}. Higher scores reflect steadier attitude
              and speed transitions across the telemetry stream.
            </p>
          </article>

          <article className="overview-card overview-card--radar">
            <header>
              <span className="overview-card__eyebrow">Flight profile</span>
              <span className="overview-card__meta">5-axis snapshot</span>
            </header>
            <FlightRadarChart metrics={metrics} analysis={analysis} />
          </article>
        </div>

        <div className="overview-grid__right">
          <article className="overview-card overview-card--map">
            <header>
              <span className="overview-card__eyebrow">2D route preview</span>
              <span className="overview-card__meta">
                {path.length.toLocaleString()} telemetry points
              </span>
            </header>
            <div className="overview-map-body">
              {path.length > 0 ? (
                <RouteMap
                  routes={[{ path, color: "#FF6B1A" }]}
                  structures={structures}
                  homePoint={homePoint}
                  height="100%"
                />
              ) : (
                <p className="empty-state">No telemetry points for preview.</p>
              )}
            </div>
          </article>

          {hasWeather && weather && (
            <article className="overview-card overview-card--weather">
              <header>
                <span className="overview-card__eyebrow">Weather</span>
                <span className="overview-card__meta">
                  {weather.sample_count} sample{weather.sample_count === 1 ? "" : "s"}
                </span>
              </header>
              <WeatherCard summary={weather} compact />
            </article>
          )}
        </div>
      </div>
    </section>
  );
}

function InsightTab({ flightId }: { flightId: number }) {
  const queryClient = useQueryClient();
  const [trainingOpen, setTrainingOpen] = useState(false);
  const aiQ = useQuery({
    queryKey: ["flight", flightId, "ai"],
    queryFn: () => getFlightAiFeedback(flightId),
    retry: false,
  });
  const generate = useMutation({
    mutationFn: () => generateFlightAiFeedback(flightId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flight", flightId, "ai"] });
    },
  });

  const notFound = aiQ.error instanceof HttpError && aiQ.error.status === 404;
  const data = aiQ.data;
  const ready = data?.status === "ready";
  const status = data?.status ?? (notFound ? "not_requested" : null);
  const canGenerate = status === "not_requested" || status === "failed" || notFound;

  return (
    <section className="tab-panel active">
      <h2>Pilot Feedback</h2>
      {aiQ.isLoading && !notFound && <p className="empty-state">Loading feedback…</p>}
      {ready && data ? (
        <div className="insight-v2">
          <div className="insight-summary">
            <p>{data.summary}</p>
            {data.generated_at && (
              <span className="insight-meta">Generated {formatDate(data.generated_at)}</span>
            )}
          </div>

          {((data as unknown as { weaknesses?: string[] }).weaknesses?.[0]) && (
            <div className="insight-training-card">
              <div className="insight-training-copy">
                <span className="insight-training-label">Mock Training Focus</span>
                <p className="insight-training-summary">
                  Worst mistake to fix first: {(data as unknown as { weaknesses: string[] }).weaknesses[0]}
                </p>
              </div>
              <button
                className="button mockup insight-training-button"
                type="button"
                onClick={() => setTrainingOpen(true)}
              >
                Generate training
              </button>
            </div>
          )}

          {(data.strengths?.length ||
            (data as unknown as { weaknesses?: string[] }).weaknesses?.length) && (
            <div className="insight-columns">
              {data.strengths && data.strengths.length > 0 && (
                <div className="insight-col">
                  <h3 className="insight-col-title insight-col-title--good">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Strengths
                  </h3>
                  {data.strengths.map((point, idx) => (
                    <div key={idx} className="insight-point insight-point--good">
                      <span className="insight-point-num">{idx + 1}</span>
                      <p>{point}</p>
                    </div>
                  ))}
                </div>
              )}

              {((data as unknown as { weaknesses?: string[] }).weaknesses?.length ?? 0) > 0 && (
                <div className="insight-col">
                  <h3 className="insight-col-title insight-col-title--warn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Areas for Improvement
                  </h3>
                  {(data as unknown as { weaknesses: string[] }).weaknesses.map((point, idx) => (
                    <div key={idx} className="insight-point insight-point--warn">
                      <span className="insight-point-num">{idx + 1}</span>
                      <p>{point}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <p className="insight-disclaimer">
            AI-generated from telemetry data — review with a qualified instructor before using for
            official assessment.
          </p>
        </div>
      ) : (
        <div className="insight-v2 insight-v2--empty">
          <div className="insight-empty-msg">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 2a4 4 0 0 1 4 4v1a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2V6a4 4 0 0 1 4-4z" />
              <path d="M9 18h6" />
              <path d="M10 22h4" />
              <path d="M12 14v4" />
            </svg>
            <div>
              <h3>AI Insight</h3>
              <p>
                {(data as unknown as { message?: string } | undefined)?.message ??
                  "AI feedback is not available for this entity."}
              </p>
            </div>
          </div>
          {canGenerate && (
            <button
              className="button primary"
              type="button"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
            >
              {generate.isPending
                ? "Generating…"
                : status === "failed"
                  ? "Retry AI feedback"
                  : "Generate AI feedback"}
            </button>
          )}
        </div>
      )}
      <TrainingPopup
        flightId={flightId}
        focusText={
          (ready &&
            (data as unknown as { weaknesses?: string[] } | undefined)?.weaknesses?.[0]) ||
          ""
        }
        open={trainingOpen}
        onClose={() => setTrainingOpen(false)}
      />
    </section>
  );
}

interface CheckpointStruct {
  key: string | number | null;
  lat: number;
  lon: number;
}

function ReplayTab({ flightId }: { flightId: number }) {
  const replayQ = useQuery({
    queryKey: ["flight", flightId, "replay-2d"],
    queryFn: () => getFlightReplay2d(flightId),
  });
  const detailQ = useQuery({
    queryKey: ["flight", flightId],
    queryFn: () => getFlightDetail(flightId),
  });
  const authOptionsQ = useQuery({
    queryKey: ["auth", "options"],
    queryFn: getAuthOptions,
    staleTime: 5 * 60_000,
  });
  const userInputsQ = useQuery({
    queryKey: ["flight", flightId, "user-inputs"],
    queryFn: () => getFlightUserInputs(flightId),
    staleTime: 5 * 60_000,
  });

  const points: TimedPoint[] = useMemo(
    () => (replayQ.data?.points ?? []) as TimedPoint[],
    [replayQ.data],
  );

  const structures =
    (detailQ.data?.structures ?? []) as unknown as Parameters<
      typeof RouteMap
    >[0]["structures"];
  const homePoint =
    ((detailQ.data as unknown as {
      home_point?: { latitude: number; longitude: number; label?: string } | null;
    })?.home_point) ?? null;

  const weatherSamples =
    (replayQ.data?.weather_samples ?? []) as Array<{
      elapsed_seconds: number;
      local_air_temperature_kelvin: number | null;
      air_temperature_kelvin: number | null;
      wind_horizontal_speed_mps: number | null;
      wind_direction_deg: number | null;
      overcast_coverage_pct: number | null;
    }>;

  const player = useReplayPlayer(points);
  const ionToken = authOptionsQ.data?.cesium_ion_token ?? null;
  const [viewMode, setViewMode] = useState<ReplayViewMode>("3d");
  const [cameraMode, setCameraMode] = useState<CesiumCameraMode>("follow");
  const [mapStyle, setMapStyle] = useState<CesiumMapStyle>("photo");
  const [hudPanelOpen, setHudPanelOpen] = useState(false);
  const { config: hudConfig, toggle: toggleHud, reset: resetHud } = useHudConfig();
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () =>
      setIsFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  useEffect(() => {
    // After a fullscreen transition the browser keeps internal canvas buffers
    // (Cesium, ECharts) at the previous dimensions until a resize event fires.
    // Fire one on the next frame + a safety net 180 ms later.
    const dispatch = () => window.dispatchEvent(new Event("resize"));
    const raf = requestAnimationFrame(dispatch);
    const timer = window.setTimeout(dispatch, 180);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [isFullscreen]);

  const toggleFullscreen = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) void document.exitFullscreen();
    else void el.requestFullscreen();
  }, []);

  const lapPasses = useMemo(
    () =>
      computeLapPassTimes(
        (structures as unknown as Parameters<typeof computeLapPassTimes>[0]) ??
          [],
        points,
      ),
    [structures, points],
  );

  // Pre-compute cumulative distance per point (running sum of haversine deltas)
  const cumulativeDistances = useMemo(() => {
    const out: number[] = new Array(points.length).fill(0);
    if (points.length < 2) return out;
    let total = 0;
    const R = 6_371_000;
    for (let i = 1; i < points.length; i += 1) {
      const a = points[i - 1];
      const b = points[i];
      const lat1 = (a.latitude * Math.PI) / 180;
      const lat2 = (b.latitude * Math.PI) / 180;
      const dLat = lat2 - lat1;
      const dLon = ((b.longitude - a.longitude) * Math.PI) / 180;
      const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
      const d = 2 * R * Math.asin(Math.sqrt(h));
      total += d;
      out[i] = total;
    }
    return out;
  }, [points]);

  const progressIndex = useMemo(() => {
    if (!points.length) return 0;
    let idx = 0;
    for (let i = 0; i < points.length; i += 1) {
      if (points[i].elapsed_seconds <= player.elapsed) idx = i;
      else break;
    }
    return idx;
  }, [points, player.elapsed]);

  const cumulativeDistance = cumulativeDistances[progressIndex] ?? 0;

  const routeLatLon = useMemo<Array<[number, number]>>(
    () => points.map((p) => [p.latitude, p.longitude]),
    [points],
  );

  const checkpointMarkers = useMemo<CheckpointStruct[]>(() => {
    const list: CheckpointStruct[] = [];
    structures?.forEach((s) => {
      if (
        s.structure_kind !== "checkpoint_ring" ||
        s.latitude == null ||
        s.longitude == null
      )
        return;
      list.push({
        key:
          (s as { source_structure_id?: number | string; id?: number | string })
            .source_structure_id ??
          (s as { id?: number | string }).id ??
          null,
        lat: s.latitude,
        lon: s.longitude,
      });
    });
    return list;
  }, [structures]);

  const weatherSnapshot = useMemo(() => {
    if (!weatherSamples.length) return null;
    let nearest = weatherSamples[0];
    for (const s of weatherSamples) {
      if (s.elapsed_seconds <= player.elapsed) nearest = s;
      else break;
    }
    const tempK = nearest.local_air_temperature_kelvin ?? nearest.air_temperature_kelvin;
    return {
      temperature_c: tempK != null ? tempK - 273.15 : null,
      wind_kmh:
        nearest.wind_horizontal_speed_mps != null
          ? nearest.wind_horizontal_speed_mps * 3.6
          : null,
      wind_direction_deg: nearest.wind_direction_deg,
      cloud_pct: nearest.overcast_coverage_pct,
    };
  }, [weatherSamples, player.elapsed]);

  // Joystick samples from the User Inputs stream (tx16s = dense stick snapshots)
  const stickSamples = useMemo(() => {
    const all = userInputsQ.data?.user_inputs ?? [];
    return all
      .filter(
        (i) =>
          i.input_type === "tx16s" &&
          (i.axis_lx != null ||
            i.axis_ly != null ||
            i.axis_rx != null ||
            i.axis_ry != null),
      )
      .sort((a, b) => a.elapsed_seconds - b.elapsed_seconds);
  }, [userInputsQ.data]);

  // Raw TX16S-style stick axes are in 0..2047 with 1024 as the physical
  // neutral on all four channels (so 2047 = stick full up / full right, 0 =
  // full down / full left). Map linearly to -1..+1 using that fixed centering.
  const currentStickAxes = useMemo(() => {
    if (!stickSamples.length) return null;
    let idx = 0;
    for (let i = 0; i < stickSamples.length; i += 1) {
      if (stickSamples[i].elapsed_seconds <= player.elapsed) idx = i;
      else break;
    }
    const sample = stickSamples[idx];
    const STICK_CENTER = 1024;
    const STICK_HALF_SPAN = 1024;
    const norm = (value: number | null): number => {
      if (value == null) return 0;
      const n = (value - STICK_CENTER) / STICK_HALF_SPAN;
      return Math.max(-1, Math.min(1, n));
    };
    return {
      lx: norm(sample.axis_lx),
      ly: norm(sample.axis_ly),
      rx: norm(sample.axis_rx),
      ry: norm(sample.axis_ry),
    };
  }, [stickSamples, player.elapsed]);

  // Running max speed up to the current playback position (monotone scan)
  const maxSpeedByIndex = useMemo(() => {
    const out = new Array<number>(points.length).fill(0);
    let peak = 0;
    for (let i = 0; i < points.length; i += 1) {
      const s = points[i].speed_mps ?? 0;
      if (s > peak) peak = s;
      out[i] = peak;
    }
    return out;
  }, [points]);

  const currentMaxSpeedMps = maxSpeedByIndex[progressIndex] ?? 0;

  const animatedRoute = {
    path: routeLatLon as unknown as number[][],
    color: "#FF6B1A",
    points,
  };

  if (replayQ.isLoading) {
    return (
      <section className="tab-panel active">
        <p className="empty-state">Loading replay…</p>
      </section>
    );
  }
  if (!points.length) {
    return (
      <section className="tab-panel active">
        <p className="empty-state">No telemetry points for this flight.</p>
      </section>
    );
  }

  return (
    <section className="tab-panel active">
      <div
        className={`rstage${isFullscreen ? " is-fullscreen" : ""}`}
        ref={stageRef}
      >
        <div className="rstage-toolbar">
          <div className="rstage-toolbar-group">
            <div className="rstage-segment">
              <button
                type="button"
                className={`rstage-segment-btn${viewMode === "3d" ? " active" : ""}`}
                onClick={() => setViewMode("3d")}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
                3D
              </button>
              <button
                type="button"
                className={`rstage-segment-btn${viewMode === "2d" ? " active" : ""}`}
                onClick={() => setViewMode("2d")}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21 1 6" />
                  <line x1="8" y1="3" x2="8" y2="18" />
                  <line x1="16" y1="6" x2="16" y2="21" />
                </svg>
                2D
              </button>
            </div>
          </div>

          {viewMode === "3d" && (
            <div className="rstage-toolbar-group">
              <div className="rstage-segment">
                {(["follow", "fpv", "free"] as CesiumCameraMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`rstage-segment-btn${cameraMode === m ? " active" : ""}`}
                    onClick={() => setCameraMode(m)}
                    title={
                      m === "follow"
                        ? "Follow the drone from behind"
                        : m === "fpv"
                          ? "First-person pilot view"
                          : "Free orbit / pan"
                    }
                  >
                    {m === "follow" && "Follow"}
                    {m === "fpv" && "FPV"}
                    {m === "free" && "Free"}
                  </button>
                ))}
              </div>
              <div className="rstage-segment">
                {(["photo", "classic"] as CesiumMapStyle[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`rstage-segment-btn${mapStyle === m ? " active" : ""}`}
                    onClick={() => setMapStyle(m)}
                    title={m === "photo" ? "Aerial photomap" : "Vector street map"}
                  >
                    {m === "photo" && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    )}
                    {m === "classic" && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21 1 6" />
                        <line x1="8" y1="3" x2="8" y2="18" />
                        <line x1="16" y1="6" x2="16" y2="21" />
                      </svg>
                    )}
                    {m === "photo" ? "Aerial" : "Classic"}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={`rstage-icon-btn${hudPanelOpen ? " active" : ""}`}
                onClick={() => setHudPanelOpen((v) => !v)}
                title="HUD widgets"
                aria-label="Configure HUD"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            </div>
          )}

          <div className="rstage-toolbar-group rstage-toolbar-group--end">
            <button
              type="button"
              className="rstage-icon-btn"
              onClick={toggleFullscreen}
              title="Fullscreen"
              aria-label="Toggle fullscreen"
            >
              {isFullscreen ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                  <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                  <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                  <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 8V5a2 2 0 0 1 2-2h3" />
                  <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                  <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                  <path d="M21 16v3a2 2 0 0 1-2 2h-3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="rstage-canvas">
          {viewMode === "3d" ? (
            <>
              <Cesium3DMap
                points={points}
                color="#FF6B1A"
                structures={structures}
                homePoint={homePoint}
                ionToken={ionToken}
                height="100%"
                progressElapsed={player.elapsed}
                lapPasses={lapPasses}
                cameraMode={cameraMode}
                fov={cameraMode === "fpv" ? 115 : 70}
                mapStyle={mapStyle}
              />
              <HudWidgets
                config={hudConfig}
                point={player.currentPoint}
                elapsed={player.elapsed}
                total={player.total}
                cumulativeDistance={cumulativeDistance}
                weather={weatherSnapshot}
                routeLatLon={routeLatLon}
                checkpoints={checkpointMarkers}
                lapPasses={lapPasses}
                progressIndex={progressIndex}
                points={points}
                stickAxes={currentStickAxes}
                maxSpeedMps={currentMaxSpeedMps}
              />
              {hudPanelOpen && (
                <HudConfigPanel
                  config={hudConfig}
                  onToggle={toggleHud}
                  onReset={resetHud}
                  onClose={() => setHudPanelOpen(false)}
                />
              )}
            </>
          ) : (
            <RouteMap
              routes={[animatedRoute]}
              structures={structures}
              homePoint={homePoint}
              height="100%"
              progressElapsed={player.elapsed}
              lapPasses={lapPasses}
            />
          )}
        </div>

        <div className="rstage-bottom">
          <ReplayControls
            playing={player.playing}
            elapsed={player.elapsed}
            total={player.total}
            speed={player.speed}
            onPlay={player.play}
            onPause={player.pause}
            onSeek={player.seek}
            onSpeedChange={player.setSpeed}
            onReset={player.reset}
          />
          <TelemetryMetricChart
            points={points}
            elapsed={player.elapsed}
            onSeek={player.seek}
            height={140}
          />
        </div>
      </div>
    </section>
  );
}

function HudConfigPanel({
  config,
  onToggle,
  onReset,
  onClose,
}: {
  config: ReturnType<typeof useHudConfig>["config"];
  onToggle: (key: HudWidgetKey) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const widgets: HudWidgetKey[] = [
    "speed",
    "vspeed",
    "distance",
    "compass",
    "battery",
    "gmeter",
    "horizon",
    "inputs",
    "minimap",
    "weather",
  ];
  return (
    <div className="rhud-config" role="dialog" aria-label="HUD widgets">
      <header>
        <strong>HUD Widgets</strong>
        <button
          type="button"
          className="rhud-config-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <ul>
        {widgets.map((key) => (
          <li key={key}>
            <label>
              <input
                type="checkbox"
                checked={config[key]}
                onChange={() => onToggle(key)}
              />
              <span>{HUD_WIDGET_LABELS[key]}</span>
            </label>
          </li>
        ))}
      </ul>
      <footer>
        <button
          type="button"
          className="button ghost rhud-config-reset"
          onClick={onReset}
        >
          Reset to default
        </button>
      </footer>
    </div>
  );
}

function formatLapTime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "—";
  const abs = Math.abs(seconds);
  const mins = Math.floor(abs / 60);
  const secs = abs - mins * 60;
  if (mins > 0) {
    return `${mins}:${secs.toFixed(3).padStart(6, "0")}`;
  }
  return `${secs.toFixed(3)}s`;
}

function LapsTab({ flightId }: { flightId: number }) {
  const lapsQ = useQuery({
    queryKey: ["flight", flightId, "laps"],
    queryFn: () => getFlightLaps(flightId),
  });
  const [expandedLapId, setExpandedLapId] = useState<number | null>(null);

  if (lapsQ.isLoading) {
    return (
      <section className="tab-panel active">
        <p className="empty-state">Loading laps…</p>
      </section>
    );
  }
  if (lapsQ.isError) {
    return (
      <section className="tab-panel active">
        <div className="callout danger">
          <strong>Could not load laps</strong>
          <p>{lapsQ.error instanceof Error ? lapsQ.error.message : "Unknown error"}</p>
        </div>
      </section>
    );
  }

  const laps: FlightLap[] = lapsQ.data?.laps ?? [];
  const completeLaps = laps.filter((lap) => lap.is_complete);
  const bestLap = completeLaps.find((lap) => lap.is_best) ?? null;
  const avgLapSeconds =
    completeLaps.length > 0
      ? completeLaps.reduce((sum, lap) => sum + lap.lap_time_seconds, 0) /
        completeLaps.length
      : null;

  if (laps.length === 0) {
    return (
      <section className="tab-panel active">
        <article className="panel">
          <div className="panel-head">
            <h2>Laps</h2>
          </div>
          <p className="empty-state">No laps were detected for this flight.</p>
        </article>
      </section>
    );
  }

  return (
    <section className="tab-panel active">
      <div className="overview-metrics">
        <article className="overview-metric">
          <span>Laps</span>
          <strong>{laps.length}</strong>
        </article>
        <article className="overview-metric">
          <span>Complete</span>
          <strong>{completeLaps.length}</strong>
        </article>
        <article className="overview-metric">
          <span>Best lap</span>
          <strong>{bestLap ? formatLapTime(bestLap.lap_time_seconds) : "—"}</strong>
        </article>
        <article className="overview-metric">
          <span>Avg lap</span>
          <strong>{formatLapTime(avgLapSeconds)}</strong>
        </article>
      </div>

      <article className="panel">
        <div className="panel-head">
          <h2>Lap times</h2>
          <p>Click a lap to inspect its sectors</p>
        </div>
        <div className="table-shell">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Lap time</th>
                <th>Delta to best</th>
                <th>Checkpoints</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {laps.map((lap) => {
                const isExpanded = expandedLapId === lap.id;
                const delta =
                  bestLap && lap.is_complete
                    ? lap.lap_time_seconds - bestLap.lap_time_seconds
                    : null;
                return (
                  <Fragment key={lap.id}>
                    <tr
                      className={lap.is_best ? "lap-row lap-row--best" : "lap-row"}
                      onClick={() => setExpandedLapId(isExpanded ? null : lap.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <strong>{lap.lap_index}</strong>
                      </td>
                      <td>
                        {formatLapTime(lap.lap_time_seconds)}
                        {lap.is_best && (
                          <span className="lap-best-badge" style={{ marginLeft: 8 }}>
                            BEST
                          </span>
                        )}
                      </td>
                      <td>
                        {delta === null
                          ? "—"
                          : delta === 0
                            ? "—"
                            : `${delta > 0 ? "+" : ""}${delta.toFixed(3)}s`}
                      </td>
                      <td>
                        {lap.checkpoint_count}
                        {lap.circle_total ? ` / ${lap.circle_total}` : ""}
                      </td>
                      <td>{lap.start_elapsed_seconds.toFixed(1)}s</td>
                      <td>{lap.end_elapsed_seconds.toFixed(1)}s</td>
                      <td>
                        <span
                          className={`status-pill ${lap.is_complete ? "ready" : "pending"}`}
                        >
                          {lap.is_complete ? "Complete" : "Partial"}
                        </span>
                      </td>
                      <td>{isExpanded ? "▾" : "▸"}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="lap-sector-row">
                        <td colSpan={8}>
                          <LapSectorDetail lap={lap} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </article>
    </section>
  );
}

function LapSectorDetail({ lap }: { lap: FlightLap }) {
  if (lap.sectors.length === 0) {
    return <p className="empty-state">No sector timings available for this lap.</p>;
  }
  const fastest = Math.min(...lap.sectors.map((s) => s.duration_seconds));
  const slowest = Math.max(...lap.sectors.map((s) => s.duration_seconds));
  return (
    <div className="lap-sector-detail">
      <h4 className="lap-sector-title">
        Lap {lap.lap_index} · Sectors ({lap.sectors.length})
      </h4>
      <div className="table-shell">
        <table className="data-table">
          <thead>
            <tr>
              <th>Sector</th>
              <th>From → To</th>
              <th>Start</th>
              <th>End</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {lap.sectors.map((sector) => {
              const isFastest =
                lap.sectors.length > 1 && sector.duration_seconds === fastest;
              const isSlowest =
                lap.sectors.length > 1 && sector.duration_seconds === slowest;
              return (
                <tr
                  key={sector.sector_index}
                  className={
                    isFastest
                      ? "lap-sector--fastest"
                      : isSlowest
                        ? "lap-sector--slowest"
                        : ""
                  }
                >
                  <td>
                    <strong>{sector.sector_index}</strong>
                  </td>
                  <td>
                    CP{sector.from_circle_sequence} → CP{sector.to_circle_sequence}
                  </td>
                  <td>{sector.start_elapsed_seconds.toFixed(2)}s</td>
                  <td>{sector.end_elapsed_seconds.toFixed(2)}s</td>
                  <td>
                    <strong>{sector.duration_seconds.toFixed(3)}s</strong>
                    {isFastest && (
                      <span className="lap-best-badge" style={{ marginLeft: 8 }}>
                        FASTEST
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpertTab({ flightId, flight }: { flightId: number; flight: FlightDetail }) {
  const [subtab, setSubtab] = useState<ExpertSubtab>("performance");
  const metricsQ = useQuery({
    queryKey: ["flight", flightId, "metrics"],
    queryFn: () => getFlightMetrics(flightId),
  });
  const chartsQ = useQuery({
    queryKey: ["flight", flightId, "charts"],
    queryFn: () => getFlightCharts(flightId),
  });

  const metrics = metricsQ.data ?? null;
  const analysis = ((flight as unknown as { analysis?: Analysis | null }).analysis ?? {}) as Analysis;
  const weather = (flight.weather_summary ?? null) as Parameters<typeof WeatherCard>[0]["summary"] | null;

  return (
    <section className="tab-panel active">
      <nav className="replay-subtab-bar">
        {(["performance", "gmeter", "weather"] as ExpertSubtab[]).map((key) => (
          <button
            key={key}
            className={`replay-subtab${subtab === key ? " active" : ""}`}
            type="button"
            onClick={() => setSubtab(key)}
          >
            {key === "performance" && "Performance"}
            {key === "gmeter" && "G Meter"}
            {key === "weather" && "Weather"}
          </button>
        ))}
      </nav>

      {subtab === "performance" && <PerformanceSubtab metrics={metrics} analysis={analysis} charts={chartsQ.data ?? null} />}
      {subtab === "gmeter" && <GMeterSubtab analysis={analysis} charts={chartsQ.data ?? null} />}
      {subtab === "weather" && <WeatherSubtab summary={weather} flightId={flightId} />}
    </section>
  );
}

function PerformanceSubtab({
  metrics,
  analysis,
  charts,
}: {
  metrics: FlightMetrics | null;
  analysis: Analysis;
  charts: { samples: ChartSample[] } | null;
}) {
  const samples = charts?.samples ?? [];

  const body = document.body ? getComputedStyle(document.body) : null;
  const textMuted = body?.getPropertyValue("--text-muted").trim() || "#A0AAB8";
  const borderColor = body?.getPropertyValue("--border").trim() || "rgba(42,52,68,0.5)";

  type SeriesDef = {
    name: string;
    color: string;
    xAxisIndex: number;
    yAxisIndex: number;
    dashed?: boolean;
    areaOpacity?: number;
    value: (sample: ChartSample) => number | null;
  };

  const seriesDefs: SeriesDef[] = [
    {
      name: "Altitude (m)",
      color: "#FBBF24",
      xAxisIndex: 0,
      yAxisIndex: 0,
      areaOpacity: 0.16,
      value: (s: ChartSample) => (s.altitude_m as number | null) ?? null,
    },
    {
      name: "Relative altitude (m)",
      color: "#FF8534",
      xAxisIndex: 0,
      yAxisIndex: 1,
      dashed: true,
      value: (s: ChartSample) => (s.home_altitude_relative_m as number | null) ?? null,
    },
    {
      name: "Speed (km / h)",
      color: "#FF6B1A",
      xAxisIndex: 1,
      yAxisIndex: 2,
      areaOpacity: 0.14,
      value: (s: ChartSample) => {
        const v = s.speed_mps as number | null;
        return v == null ? null : v * MPS_TO_KMH;
      },
    },
    {
      name: "Horizontal speed (km / h)",
      color: "#A78BFA",
      xAxisIndex: 1,
      yAxisIndex: 2,
      dashed: true,
      value: (s: ChartSample) => {
        const v = s.horizontal_speed_mps as number | null;
        return v == null ? null : v * MPS_TO_KMH;
      },
    },
    {
      name: "Vertical rate (m/s)",
      color: "#38BDF8",
      xAxisIndex: 2,
      yAxisIndex: 3,
      value: (s: ChartSample) => (s.vertical_rate_mps as number | null) ?? null,
    },
    {
      name: "Heading (deg)",
      color: "#EF4444",
      xAxisIndex: 3,
      yAxisIndex: 4,
      value: (s: ChartSample) => (s.yaw_deg as number | null) ?? null,
    },
    {
      name: "Battery (%)",
      color: "#4ADE80",
      xAxisIndex: 4,
      yAxisIndex: 5,
      areaOpacity: 0.1,
      value: (s: ChartSample) =>
        normalizeFuelPercent(
          s.fuel as number | null,
          s.fuel_capacity as number | null,
        ),
    },
  ].filter((def) => samples.some((sample) => def.value(sample) != null));

  const baseAxisLabel = { color: textMuted, fontSize: 10 };
  const makeXAxis = (gridIndex: number, showLabels: boolean) => ({
    type: "value" as const,
    gridIndex,
    min: "dataMin",
    max: "dataMax",
    axisLine: { show: showLabels, lineStyle: { color: borderColor } },
    axisTick: { show: false },
    axisLabel: {
      show: showLabels,
      ...baseAxisLabel,
      formatter: (v: number) => `${v.toFixed(0)}`,
    },
    splitLine: { show: false },
    name: gridIndex === 4 ? "Time (s)" : "",
    nameLocation: "middle" as const,
    nameGap: 26,
    nameTextStyle: { color: textMuted, fontSize: 11 },
  });

  const makeYAxis = (opts: {
    gridIndex: number;
    name: string;
    color: string;
    position?: "left" | "right";
    min?: number;
    max?: number;
    formatter?: (v: number) => string;
    splitLine?: boolean;
  }) => ({
    type: "value" as const,
    gridIndex: opts.gridIndex,
    position: opts.position ?? "left",
    name: opts.name,
    nameTextStyle: { color: opts.color, fontSize: 10 },
    axisTick: { show: false },
    axisLine: { show: false },
    axisLabel: {
      ...baseAxisLabel,
      color: opts.position === "right" ? opts.color : textMuted,
      formatter: opts.formatter ?? ((v: number) => `${v.toFixed(0)}`),
    },
    min: opts.min,
    max: opts.max,
    splitLine:
      opts.splitLine === false
        ? { show: false }
        : { lineStyle: { color: borderColor, type: "dashed" } },
  });

  const option = samples.length > 0 && seriesDefs.length > 0 ? {
    animation: false,
    legend: {
      data: seriesDefs.map((s) => s.name),
      top: 6,
      left: 14,
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 12,
      textStyle: { color: textMuted, fontSize: 10 },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", lineStyle: { color: borderColor } },
      valueFormatter: (val: unknown) =>
        typeof val === "number" ? val.toFixed(2) : String(val ?? "—"),
    },
    axisPointer: { link: [{ xAxisIndex: [0, 1, 2, 3, 4] }] },
    dataZoom: [
      { type: "inside", xAxisIndex: [0, 1, 2, 3, 4] },
      {
        type: "slider",
        xAxisIndex: [0, 1, 2, 3, 4],
        height: 18,
        bottom: 6,
      },
    ],
    grid: [
      { left: 60, right: 60, top: 46, height: 78 },
      { left: 60, right: 60, top: 154, height: 78 },
      { left: 60, right: 60, top: 262, height: 78 },
      { left: 60, right: 60, top: 370, height: 62 },
      { left: 60, right: 60, top: 462, height: 62 },
    ],
    xAxis: [
      makeXAxis(0, false),
      makeXAxis(1, false),
      makeXAxis(2, false),
      makeXAxis(3, false),
      makeXAxis(4, true),
    ],
    yAxis: [
      makeYAxis({ gridIndex: 0, name: "Alt", color: "#FBBF24" }),
      makeYAxis({
        gridIndex: 0,
        name: "Rel",
        color: "#FF8534",
        position: "right",
        splitLine: false,
      }),
      makeYAxis({ gridIndex: 1, name: "Spd", color: "#FF6B1A" }),
      makeYAxis({ gridIndex: 2, name: "V/S", color: "#38BDF8" }),
      makeYAxis({ gridIndex: 3, name: "HDG", color: "#EF4444" }),
      makeYAxis({
        gridIndex: 4,
        name: "BAT",
        color: "#4ADE80",
        min: 0,
        max: 100,
        formatter: (v: number) => `${v.toFixed(0)}%`,
      }),
    ],
    series: seriesDefs.map((def) => ({
      name: def.name,
      type: "line" as const,
      xAxisIndex: def.xAxisIndex,
      yAxisIndex: def.yAxisIndex,
      showSymbol: false,
      smooth: 0.16,
      connectNulls: true,
      lineStyle: {
        width: def.dashed ? 1.4 : 2,
        color: def.color,
        type: def.dashed ? "dashed" : "solid",
      },
      itemStyle: { color: def.color },
      areaStyle: def.areaOpacity
        ? { color: def.color, opacity: def.areaOpacity }
        : undefined,
      data: samples.map((sample) => [sample.elapsed_seconds, def.value(sample)]),
    })),
  } : null;

  const metricSafe = (key: keyof FlightMetrics, digits = 0, suffix = ""): string =>
    metrics ? num(metrics[key] as number | null, digits, suffix) : "—";

  return (
    <div className="detail-subtab-panel active">
      <article className="panel chart-shell">
        <div className="panel-head">
          <h2>Time-aligned telemetry</h2>
          <p>
            Altitude, altitude vs home point, speed, vertical rate, heading, and battery over time.
          </p>
        </div>
        <div className="chart-panel chart-panel-telemetry">
          {option ? (
            <ReactECharts option={option} notMerge style={{ height: 560, width: "100%" }} />
          ) : (
            <p className="empty-state">No telemetry samples for charts.</p>
          )}
        </div>
      </article>

      <div className="kpi-ribbon">
        <article className="kpi-card accent-distance">
          <span>Track length</span>
          <strong>{metricSafe("total_distance_m", 0, " m")}</strong>
          <small>geodesic approximation</small>
        </article>
        <article className="kpi-card accent-speed">
          <span>Speed envelope</span>
          <strong>{metrics ? `${speedKmh(metrics.avg_speed_mps, 1, "")} km/h avg` : "—"}</strong>
          <small>
            {metrics
              ? `${speedKmh(metrics.min_speed_mps, 1, "")} / ${speedKmh(metrics.max_speed_mps, 1, " km/h")}`
              : "—"}
          </small>
        </article>
        <article className="kpi-card accent-altitude">
          <span>Altitude (MSL)</span>
          <strong>{metricSafe("max_altitude_m", 0, " m ceiling")}</strong>
          <small>
            {metrics
              ? `${num(metrics.min_altitude_m, 0)} / ${num(metrics.avg_altitude_m, 0, " mean")}`
              : "—"}
          </small>
        </article>
        <article className="kpi-card accent-climb">
          <span>Climb / descent</span>
          <strong>{metricSafe("total_climb_m", 0, " m ↑")}</strong>
          <small>{metricSafe("total_descent_m", 0, " m ↓")}</small>
        </article>
        <article className="kpi-card accent-hover">
          <span>Hover ratio</span>
          <strong>
            {metrics?.hover_ratio != null ? `${(metrics.hover_ratio * 100).toFixed(0)}%` : "—"}
          </strong>
          <small>{metricSafe("telemetry_rate_hz", 1, " Hz")} sample rate</small>
        </article>
      </div>

      <div className="grid three-up">
        <article className="analysis-card">
          <div className="panel-head">
            <h2>Systems state</h2>
            <p>Platform and onboard state over the flight</p>
          </div>
          <dl className="summary-stack">
            <div>
              <dt>Engine active</dt>
              <dd>{num(analysis.systems?.engine_on_ratio_pct, 0, "%")}</dd>
            </div>
            <div>
              <dt>Fuel start</dt>
              <dd>{num(analysis.systems?.fuel_start_pct, 0, "%")}</dd>
            </div>
            <div>
              <dt>Fuel end</dt>
              <dd>{num(analysis.systems?.fuel_end_pct, 0, "%")}</dd>
            </div>
            <div>
              <dt>Fuel delta</dt>
              <dd>{num(analysis.systems?.fuel_delta_pct, 1, " pp")}</dd>
            </div>
            <div>
              <dt>Peak model speed</dt>
              <dd>{speedKmh(analysis.systems?.max_model_speed_mps)}</dd>
            </div>
          </dl>
        </article>

        <article className="analysis-card">
          <div className="panel-head">
            <h2>Computed performance</h2>
            <p>Derived values from the telemetry stream</p>
          </div>
          <dl className="summary-stack">
            <div>
              <dt>Telemetry rate</dt>
              <dd>{metricSafe("telemetry_rate_hz", 1, " Hz")}</dd>
            </div>
            <div>
              <dt>Heading change</dt>
              <dd>{metricSafe("heading_change_deg", 0, "°")}</dd>
            </div>
            <div>
              <dt>Peak turn rate</dt>
              <dd>{metricSafe("max_turn_rate_deg_s", 1, " °/s")}</dd>
            </div>
            <div>
              <dt>Peak acceleration</dt>
              <dd>{metricSafe("max_acceleration_mps2", 1, " m/s²")}</dd>
            </div>
            <div>
              <dt>Smoothness score</dt>
              <dd>{metricSafe("smoothness_score", 0, " /100")}</dd>
            </div>
            <div>
              <dt>Longest data gap</dt>
              <dd>{metricSafe("max_gap_seconds", 2, " s")}</dd>
            </div>
          </dl>
        </article>

        <article className="analysis-card compact">
          <div className="panel-head">
            <h2>Telemetry quality</h2>
            <p>How trustworthy the captured stream is</p>
          </div>
          <dl className="summary-stack">
            <div>
              <dt>Sample count</dt>
              <dd>{analysis.data_quality?.sample_count ?? "—"}</dd>
            </div>
            <div>
              <dt>Median interval</dt>
              <dd>{num(analysis.data_quality?.median_interval_s, 3, " s")}</dd>
            </div>
            <div>
              <dt>Max gap</dt>
              <dd>{num(analysis.data_quality?.max_gap_s, 2, " s")}</dd>
            </div>
            <div>
              <dt>Position coverage</dt>
              <dd>{num(analysis.data_quality?.position_coverage_pct, 0, "%")}</dd>
            </div>
            <div>
              <dt>Orientation coverage</dt>
              <dd>{num(analysis.data_quality?.orientation_coverage_pct, 0, "%")}</dd>
            </div>
            <div>
              <dt>Duplicate timestamps</dt>
              <dd>{analysis.data_quality?.duplicate_timestamps ?? "—"}</dd>
            </div>
          </dl>
        </article>
      </div>
    </div>
  );
}

function GMeterSubtab({
  analysis,
  charts,
}: {
  analysis: Analysis;
  charts: { samples: ChartSample[] } | null;
}) {
  const samples = charts?.samples ?? [];

  // Compute per-sample G load using the same formula as the replay HUD so the
  // two views always agree. Longitudinal G = d(speed)/dt / g. Lateral G =
  // v · yawRate / g (centripetal). Total G = √(lat² + lon²). A 5-sample moving
  // average is then applied to suppress single-frame numerical spikes from
  // noisy telemetry (short dt × noisy speed / yaw can produce unrealistic
  // transients that don't reflect actual load on the airframe).
  const gSeries = useMemo(() => {
    const G = 9.80665;
    const rawLon: Array<number | null> = [null];
    const rawLat: Array<number | null> = [null];
    for (let i = 1; i < samples.length; i += 1) {
      const a = samples[i - 1];
      const b = samples[i];
      const dt = (Number(b.elapsed_seconds) - Number(a.elapsed_seconds)) || 0;
      const sa = (a.speed_mps as number | null) ?? null;
      const sb = (b.speed_mps as number | null) ?? null;
      const ya = (a.yaw_deg as number | null) ?? null;
      const yb = (b.yaw_deg as number | null) ?? null;
      if (!(dt > 0) || sa == null || sb == null) {
        rawLon.push(null);
        rawLat.push(null);
        continue;
      }
      const longG = (sb - sa) / dt / G;
      let latG = 0;
      if (ya != null && yb != null) {
        let dyaw = yb - ya;
        while (dyaw > 180) dyaw -= 360;
        while (dyaw < -180) dyaw += 360;
        const yawRate = (dyaw * Math.PI) / 180 / dt;
        const vMid = (sa + sb) / 2;
        latG = (vMid * yawRate) / G;
      }
      rawLon.push(longG);
      rawLat.push(latG);
    }
    const smooth = (src: Array<number | null>): Array<number | null> => {
      const half = 2;
      const n = src.length;
      const out = new Array<number | null>(n);
      for (let i = 0; i < n; i += 1) {
        let sum = 0;
        let count = 0;
        for (let j = Math.max(0, i - half); j <= Math.min(n - 1, i + half); j += 1) {
          const v = src[j];
          if (v != null && Number.isFinite(v)) {
            sum += v;
            count += 1;
          }
        }
        out[i] = count > 0 ? sum / count : null;
      }
      return out;
    };
    const lon = smooth(rawLon);
    const lat = smooth(rawLat);
    const total: Array<number | null> = new Array(samples.length).fill(null);
    let peakTotal = 0;
    for (let i = 0; i < samples.length; i += 1) {
      const lo = lon[i];
      const la = lat[i];
      if (lo == null || la == null) continue;
      const tot = Math.hypot(lo, la);
      total[i] = tot;
      if (tot > peakTotal) peakTotal = tot;
    }
    return { lon, lat, total, peak: peakTotal };
  }, [samples]);

  // Prefer the replay-consistent peak when available; fall back to the
  // server-computed longitudinal peak.
  const backendPeakG =
    analysis.safety?.peak_acceleration_mps2 != null
      ? analysis.safety.peak_acceleration_mps2 / 9.81
      : null;
  const peakG = gSeries.peak > 0 ? gSeries.peak : backendPeakG;

  const option = samples.length > 1 ? {
    grid: { top: 24, right: 20, bottom: 30, left: 52 },
    tooltip: { trigger: "axis" },
    legend: {
      top: 0,
      textStyle: { color: "#9CA3AF", fontSize: 10 },
      itemWidth: 12,
      itemHeight: 8,
    },
    xAxis: {
      type: "category",
      data: samples.map((s) => s.elapsed_seconds),
      axisLabel: { fontSize: 10, formatter: (v: string) => `${Number(v).toFixed(0)}s` },
    },
    yAxis: { type: "value", scale: true, axisLabel: { formatter: "{value} g" } },
    series: [
      {
        name: "Total G",
        type: "line",
        data: gSeries.total,
        smooth: 0.2,
        showSymbol: false,
        lineStyle: { width: 2.4, color: "#FF6B1A" },
      },
      {
        name: "Longitudinal",
        type: "line",
        data: gSeries.lon,
        smooth: 0.2,
        showSymbol: false,
        lineStyle: { width: 1.4, color: "#FBBF24" },
      },
      {
        name: "Lateral",
        type: "line",
        data: gSeries.lat,
        smooth: 0.2,
        showSymbol: false,
        lineStyle: { width: 1.4, color: "#60A5FA" },
      },
    ],
  } : null;

  return (
    <div className="detail-subtab-panel active">
      <p className="expert-intro">
        G-meter view turns speed-change dynamics into longitudinal load factor over time. Use it
        to spot sharp control inputs, repeated spikes, and periods where maneuvering intensity
        starts to climb.
      </p>

      <div className="kpi-ribbon">
        <article className="kpi-card accent-speed">
          <span>Peak load factor</span>
          <strong>{num(peakG, 2, " g")}</strong>
          <small>{num(analysis.safety?.peak_acceleration_mps2, 1, " m/s²")}</small>
        </article>
        <article className="kpi-card accent-turn">
          <span>High-load spikes</span>
          <strong>{analysis.safety?.high_acceleration_events ?? "—"}</strong>
          <small>events above the training threshold</small>
        </article>
        <article className="kpi-card accent-hover">
          <span>Rapid turn share</span>
          <strong>{num(analysis.safety?.rapid_turn_exposure_pct, 0, "%")}</strong>
          <small>{num(analysis.safety?.peak_turn_rate_deg_s, 1, " °/s peak")}</small>
        </article>
        <article className="kpi-card accent-altitude">
          <span>Hard bank share</span>
          <strong>{num(analysis.safety?.hard_bank_exposure_pct, 0, "%")}</strong>
          <small>{num(analysis.attitude?.max_bank_abs_deg, 1, "° peak")}</small>
        </article>
        <article className="kpi-card accent-climb">
          <span>High descent share</span>
          <strong>{num(analysis.safety?.high_descent_exposure_pct, 0, "%")}</strong>
          <small>{num(analysis.safety?.high_descent_exposure_s, 1, " s flagged")}</small>
        </article>
      </div>

      <div className="grid two-up">
        <article className="panel chart-shell">
          <div className="panel-head">
            <h2>G-load development</h2>
            <p>Signed acceleration and load factor over time.</p>
          </div>
          <div className="chart-panel">
            {option ? (
              <ReactECharts option={option} notMerge style={{ height: 280, width: "100%" }} />
            ) : (
              <p className="empty-state">No telemetry samples.</p>
            )}
          </div>
        </article>
        <article className="analysis-card">
          <div className="panel-head">
            <h2>Overload analysis</h2>
            <p>Derived control-intensity markers from the telemetry stream</p>
          </div>
          <dl className="summary-stack">
            <div>
              <dt>Peak load factor</dt>
              <dd>{num(peakG, 2, " g")}</dd>
            </div>
            <div>
              <dt>Peak turn rate</dt>
              <dd>{num(analysis.safety?.peak_turn_rate_deg_s, 1, " °/s")}</dd>
            </div>
            <div>
              <dt>High-load spikes</dt>
              <dd>{analysis.safety?.high_acceleration_events ?? "—"}</dd>
            </div>
            <div>
              <dt>Rapid turn exposure</dt>
              <dd>{num(analysis.safety?.rapid_turn_exposure_pct, 0, "%")}</dd>
            </div>
            <div>
              <dt>Hard bank exposure</dt>
              <dd>{num(analysis.safety?.hard_bank_exposure_pct, 0, "%")}</dd>
            </div>
            <div>
              <dt>Damage peak</dt>
              <dd>{num(analysis.safety?.damage_peak_pct, 0, "%")}</dd>
            </div>
            <div>
              <dt>Low fuel exposure</dt>
              <dd>{num(analysis.safety?.low_fuel_exposure_pct, 0, "%")}</dd>
            </div>
            <div>
              <dt>Engine-off aloft</dt>
              <dd>{num(analysis.safety?.engine_off_aloft_pct, 0, "%")}</dd>
            </div>
          </dl>
        </article>
      </div>
    </div>
  );
}

function WeatherSubtab({
  summary,
  flightId,
}: {
  summary: Parameters<typeof WeatherCard>[0]["summary"] | null;
  flightId: number;
}) {
  const replayQ = useQuery({
    queryKey: ["flight", flightId, "replay-2d"],
    queryFn: () => getFlightReplay2d(flightId),
  });
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const samples = replayQ.data?.weather_samples ?? [];
  const pageCount = Math.max(1, Math.ceil(samples.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * pageSize;
  const pageRows = samples.slice(start, start + pageSize);

  if (!summary || !summary.sample_count) {
    return (
      <div className="detail-subtab-panel active">
        <article className="panel">
          <div className="panel-head">
            <h2>Weather conditions</h2>
          </div>
          <p className="empty-state">No detailed weather samples were captured for this flight.</p>
        </article>
      </div>
    );
  }

  return (
    <div className="detail-subtab-panel active">
      <div className="grid two-up">
        <WeatherCard summary={summary} />
        <article className="analysis-card">
          <div className="panel-head">
            <h2>Scenario clock</h2>
            <p>Weather stream timing and pacing</p>
          </div>
          <dl className="summary-stack summary-stack-readable">
            <div>
              <dt>Samples</dt>
              <dd>{summary.sample_count}</dd>
            </div>
          </dl>
        </article>
      </div>

      <article className="panel">
        <div className="panel-head">
          <h2>Weather sample log</h2>
          <p>Detailed samples from the scenario stream</p>
        </div>
        <div className="table-toolbar">
          <p className="table-meta">
            Showing {samples.length ? start + 1 : 0}–{start + pageRows.length} of {samples.length} samples
          </p>
          {pageCount > 1 && (
            <div className="table-pager">
              <button
                className="button ghost"
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </button>
              <span className="table-pager-status">
                Page {currentPage} of {pageCount}
              </span>
              <button
                className="button ghost"
                type="button"
                disabled={currentPage >= pageCount}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
        <div className="table-shell">
          <table className="data-table">
            <thead>
              <tr>
                <th>Elapsed</th>
                <th>Temperature</th>
                <th>Wind</th>
                <th>Cloud cover</th>
                <th>Fog</th>
                <th>Precipitation</th>
                <th>Pressure</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((sample, idx) => (
                <tr key={start + idx}>
                  <td>{sample.elapsed_seconds.toFixed(1)} s</td>
                  <td>
                    {sample.local_air_temperature_kelvin !== null
                      ? `${(sample.local_air_temperature_kelvin - 273.15).toFixed(1)} °C`
                      : sample.air_temperature_kelvin !== null
                        ? `${(sample.air_temperature_kelvin - 273.15).toFixed(1)} °C`
                        : "—"}
                  </td>
                  <td>
                    {sample.wind_horizontal_speed_mps !== null
                      ? `${(sample.wind_horizontal_speed_mps * 3.6).toFixed(1)} km/h`
                      : "—"}
                    {sample.wind_direction_deg !== null &&
                      ` · ${sample.wind_direction_deg.toFixed(0)}°`}
                  </td>
                  <td>
                    {sample.overcast_coverage_pct !== null
                      ? `${sample.overcast_coverage_pct.toFixed(0)}%`
                      : sample.overcast_density !== null
                        ? sample.overcast_density.toFixed(2)
                        : "—"}
                  </td>
                  <td>
                    {sample.fog_density !== null ? sample.fog_density.toFixed(2) : "—"}
                  </td>
                  <td>{sample.precipitation_type ?? "—"}</td>
                  <td>
                    {sample.air_pressure_hpa !== null
                      ? `${sample.air_pressure_hpa.toFixed(1)} hPa`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </div>
  );
}

function formatElapsedTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds - mins * 60;
  return `${mins}:${secs.toFixed(3).padStart(6, "0")}`;
}

function formatAxis(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toString();
}

function formatButtons(buttons: Record<string, unknown> | null): string {
  if (!buttons) return "—";
  const pressed = Object.entries(buttons)
    .filter(([, v]) => v === true || v === 1 || (typeof v === "number" && v > 0))
    .map(([k]) => k);
  if (pressed.length === 0) return "—";
  return pressed.join(", ");
}

const USER_INPUT_TYPE_FILTERS: Array<{ key: string; label: string }> = [
  { key: "all", label: "All" },
  { key: "tx16s", label: "Sticks" },
  { key: "input_changed", label: "Actions" },
  { key: "input_processed", label: "Events" },
  { key: "window_procedure", label: "Keyboard" },
];

const USER_INPUT_PAGE_SIZES = [25, 50, 100, 200];

function UserInputsTab({ flightId }: { flightId: number }) {
  const inputsQ = useQuery({
    queryKey: ["flight", flightId, "user-inputs"],
    queryFn: () => getFlightUserInputs(flightId),
  });
  const [filter, setFilter] = useState<string>("all");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  useEffect(() => {
    setPage(1);
  }, [filter, pageSize]);

  if (inputsQ.isLoading) {
    return (
      <section className="tab-panel active">
        <p className="empty-state">Loading user inputs…</p>
      </section>
    );
  }
  if (inputsQ.isError) {
    return (
      <section className="tab-panel active">
        <div className="callout danger">
          <strong>Could not load user inputs</strong>
          <p>{inputsQ.error instanceof Error ? inputsQ.error.message : "Unknown error"}</p>
        </div>
      </section>
    );
  }

  const allInputs: FlightUserInput[] = inputsQ.data?.user_inputs ?? [];
  const inputs =
    filter === "all"
      ? allInputs
      : allInputs.filter((input) => input.input_type === filter);

  const typeCounts = allInputs.reduce<Record<string, number>>((acc, input) => {
    acc[input.input_type] = (acc[input.input_type] ?? 0) + 1;
    return acc;
  }, {});

  if (allInputs.length === 0) {
    return (
      <section className="tab-panel active">
        <article className="panel">
          <div className="panel-head">
            <h2>User Inputs</h2>
          </div>
          <p className="empty-state">No user inputs were recorded for this flight.</p>
        </article>
      </section>
    );
  }

  const firstElapsed = allInputs[0].elapsed_seconds;
  const lastElapsed = allInputs[allInputs.length - 1].elapsed_seconds;
  const uniqueDevices = new Set(
    allInputs
      .map((input) => input.display_name ?? input.device_name)
      .filter((name): name is string => Boolean(name)),
  );

  const totalPages = Math.max(1, Math.ceil(inputs.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, inputs.length);
  const pageRows = inputs.slice(pageStart, pageEnd);

  return (
    <section className="tab-panel active">
      <div className="overview-metrics">
        <article className="overview-metric">
          <span>Total events</span>
          <strong>{allInputs.length}</strong>
        </article>
        <article className="overview-metric">
          <span>Showing</span>
          <strong>{inputs.length}</strong>
        </article>
        <article className="overview-metric">
          <span>Devices</span>
          <strong>{uniqueDevices.size || "—"}</strong>
        </article>
        <article className="overview-metric">
          <span>Time span</span>
          <strong>
            {formatElapsedTime(firstElapsed)} → {formatElapsedTime(lastElapsed)}
          </strong>
        </article>
      </div>

      <article className="panel">
        <div className="panel-head">
          <h2>User Inputs Timeline</h2>
          <p>All pilot inputs recorded during this flight, ordered by time.</p>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            padding: "0 16px 12px",
          }}
        >
          {USER_INPUT_TYPE_FILTERS.map((item) => {
            const count =
              item.key === "all" ? allInputs.length : typeCounts[item.key] ?? 0;
            if (item.key !== "all" && count === 0) return null;
            return (
              <button
                key={item.key}
                type="button"
                className={`tab-button${filter === item.key ? " active" : ""}`}
                onClick={() => setFilter(item.key)}
              >
                {item.label} ({count})
              </button>
            );
          })}
        </div>

        <div className="table-shell">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>Device</th>
                <th>Action / Event</th>
                <th>Value</th>
                <th>LX</th>
                <th>LY</th>
                <th>RX</th>
                <th>RY</th>
                <th>Buttons</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((input) => (
                <tr key={input.id}>
                  <td>{formatElapsedTime(input.elapsed_seconds)}</td>
                  <td>
                    <span className="status-pill ready">{input.input_type}</span>
                  </td>
                  <td>{input.display_name ?? input.device_name ?? "—"}</td>
                  <td>
                    {input.action_name ??
                      input.event_name ??
                      input.key_name ??
                      "—"}
                  </td>
                  <td>
                    {input.action_value !== null && input.action_value !== undefined
                      ? input.action_value.toFixed(3)
                      : input.is_active !== null && input.is_active !== undefined
                        ? input.is_active
                          ? "ON"
                          : "OFF"
                        : "—"}
                  </td>
                  <td>{formatAxis(input.axis_lx)}</td>
                  <td>{formatAxis(input.axis_ly)}</td>
                  <td>{formatAxis(input.axis_rx)}</td>
                  <td>{formatAxis(input.axis_ry)}</td>
                  <td>{formatButtons(input.buttons)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            padding: "12px 16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ opacity: 0.75 }}>Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="flight-entity-select"
            >
              {USER_INPUT_PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div style={{ opacity: 0.75 }}>
            {inputs.length === 0
              ? "0 of 0"
              : `${pageStart + 1}–${pageEnd} of ${inputs.length}`}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              className="button ghost"
              disabled={currentPage <= 1}
              onClick={() => setPage(1)}
            >
              « First
            </button>
            <button
              type="button"
              className="button ghost"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            >
              ‹ Prev
            </button>
            <span style={{ minWidth: 90, textAlign: "center" }}>
              Page {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              className="button ghost"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
            >
              Next ›
            </button>
            <button
              type="button"
              className="button ghost"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              Last »
            </button>
          </div>
        </div>
      </article>
    </section>
  );
}

// Guard against unused imports in specific build modes
void useEffect;
